import type { Control } from "react-hook-form";
import { useController } from "react-hook-form";

import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import { getIcon } from "@/components/interactions/_utils";
import { InteractionMode } from "@/components/interactions/types";
import ListItem from "@/components/ListItem";
import { ListItemSkeleton } from "@/components/Skeleton";

import {
  careerPlanOnboardingInteractionCategoryLabel,
  careerPlanOnboardingInteractionDate,
  careerPlanOnboardingInteractionHeadline,
  careerPlanOnboardingInteractionSummary,
  type CareerPlanOnboardingFormValues
} from "./careerPlanOnboarding";
import type { CareerPlanOnboardingInteraction } from "./queries";

export default function CareerPlanOnboardingInteractionsStep({
  control,
  interactions,
  loading,
  errorMessage
}: {
  control: Control<CareerPlanOnboardingFormValues>;
  interactions: CareerPlanOnboardingInteraction[];
  loading: boolean;
  errorMessage?: string;
}) {
  const { field } = useController({ control, name: "selectedInteractionIds" });

  if (loading) {
    return (
      <div data-testid="career-plan-onboarding-loading">
        <ListItemSkeleton rows={4} />
      </div>
    );
  }

  if (errorMessage) {
    return <ErrorBoundary variant="inline" title="Unable to Load Interactions" message={errorMessage} />;
  }

  const selectedIds = field.value;

  return (
    <div className="flex flex-col gap-6" data-testid="career-plan-onboarding-interactions">
      <p className="text-headline-small">Select the interactions that should shape this plan.</p>
      {interactions.length === 0 ? (
        <div data-testid="career-plan-onboarding-interactions-empty">
          <EmptyState
            iconName="3p"
            title="No Recent Interactions"
            caption="This person has no interactions to include yet."
          />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {interactions.map((interaction) => {
            const headline = careerPlanOnboardingInteractionHeadline(interaction);
            const categoryLabel = careerPlanOnboardingInteractionCategoryLabel(interaction);
            const checked = selectedIds.includes(interaction.id);
            return (
              <ListItem
                key={interaction.id}
                className="!my-0"
                iconName={getIcon(interaction.mode as InteractionMode)}
                iconBackground
                longContent
                overline={careerPlanOnboardingInteractionDate(interaction)}
                headline={headline}
                supportingText={careerPlanOnboardingInteractionSummary(interaction)}
                tagText={categoryLabel || undefined}
                tagType="secondary"
                useCheckbox
                checkboxChecked={checked}
                checkboxName={`career-plan-onboarding-interaction-${interaction.id}`}
                onCheckboxChange={(nextChecked) => {
                  if (nextChecked) {
                    field.onChange(
                      selectedIds.includes(interaction.id) ? selectedIds : [...selectedIds, interaction.id]
                    );
                    return;
                  }
                  field.onChange(selectedIds.filter((id) => id !== interaction.id));
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
